import { useEffect, useState } from "react";
import { Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import {
  buildShareUrl,
  shareInvite,
  fetchMyReferralStats,
} from "../lib/referrals";

/**
 * "Invite a friend" button. Uses the native share sheet when available,
 * otherwise copies the referral link to the clipboard.
 *
 * Two visual variants:
 *   • variant="tile"    → the large tile shown on Dashboard.
 *   • variant="inline"  → the compact card shown in Settings and Account.
 *
 * Automatically fetches the user's current referral count and shows it
 * once loaded (so the button doubles as social proof: "N friends invited").
 */
export const ShareInviteButton = ({ variant = "inline" }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(null);
  const userId = user?.user_id || null;

  useEffect(() => {
    if (!userId) return;
    fetchMyReferralStats()
      .then((s) => setCount(s?.count ?? 0))
      .catch(() => setCount(null));
  }, [userId]);

  const handleShare = async () => {
    if (!userId) {
      toast.error("Please sign in first.");
      return;
    }
    setBusy(true);
    try {
      const res = await shareInvite({ userId });
      if (res.ok && res.method === "share") {
        toast.success("Thanks for spreading calm.");
      } else if (res.ok && res.method === "clipboard") {
        toast.success("Invite link copied — paste it anywhere.");
      } else if (res.cancelled) {
        // User dismissed native share sheet — no toast noise.
      } else {
        toast.error("Could not open share sheet.");
      }
    } finally {
      setBusy(false);
    }
  };

  const countLine =
    count === null
      ? null
      : count > 0
      ? `${count} ${count === 1 ? "friend" : "friends"} invited so far.`
      : "Be the first to share it with someone who could use two calm minutes.";

  if (variant === "tile") {
    return (
      <button
        onClick={handleShare}
        disabled={busy || !userId}
        className="group text-left w-full bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60"
        data-testid="share-invite-tile"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#EAF0F9] flex items-center justify-center flex-shrink-0">
            {busy ? (
              <Loader2 className="w-5 h-5 text-[#3090A8] animate-spin" />
            ) : (
              <Share2 className="w-5 h-5 text-[#3090A8]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-outfit font-medium text-lg text-[#1B2349]">
              Invite a friend
            </div>
            <p className="text-sm text-[#718096] mt-1">
              {countLine || "Share Bilateral Calm with someone who needs it."}
            </p>
          </div>
        </div>
      </button>
    );
  }

  // inline / card variant
  return (
    <div
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-6 animate-fade-in-up"
      data-testid="share-invite-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#EAF0F9] flex items-center justify-center flex-shrink-0">
          <Share2 className="w-5 h-5 text-[#3090A8]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1">
            Share Bilateral Calm
          </h2>
          <p className="text-sm text-[#718096] mb-4">
            {countLine ||
              "Share Bilateral Calm with someone who could use two calm minutes."}
          </p>
          <button
            onClick={handleShare}
            disabled={busy || !userId}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1B2349] text-white font-medium hover:bg-[#0F1838] transition-colors disabled:opacity-60"
            data-testid="share-invite-btn"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
            Invite a friend
          </button>
          {userId && (
            <p
              className="mt-3 text-xs text-[#718096] break-all"
              data-testid="share-invite-url"
            >
              {buildShareUrl(userId)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareInviteButton;
